import { ReactElement } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { Box, Chip, Tooltip, Typography } from '@mui/material'
import { useGetThreadUsage } from '@api/agent-runs/agent-runs'
import { useSnackbar } from '@ui/snackbar'
import useSeesChatInternals from '../hooks/useSeesChatInternals'
import { formatChatCredits, formatChatCost, formatRequestCount, formatTokenCount } from '../utils'

interface Props {
  threadId: string
  isRunning: boolean
}

/**
 * What the open chat has cost up to its latest answer, summed over all its
 * turns (`GET /agent-runs/threads/{id}/usage`). The query is disabled while a
 * turn runs and re-enabled when it ends; React Query refetches a stale query
 * when it becomes enabled again, so the total updates after every answer
 * without an effect. Window focus does not refetch: the total only changes
 * when a turn ends. Users who are neither developers nor admins see it in credits, with
 * a plain tooltip.
 */
const ThreadCostChip = ({ threadId, isRunning }: Props): ReactElement | null => {
  const { enqueueSnackbar } = useSnackbar()
  const seesInternals = useSeesChatInternals()
  const { data } = useGetThreadUsage(threadId, {
    query: {
      enabled: !isRunning,
      staleTime: 0,
      refetchOnWindowFocus: false,
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'KI-Chat: Kosten konnten nicht geladen werden'), {
          variant: 'error',
        })
      },
    },
  })

  if (!data || data.runs === 0) return null

  const hasTurnsWithoutCost = data.runs_without_cost > 0
  const label = seesInternals
    ? formatChatCost(data.cost_usd, hasTurnsWithoutCost)
    : formatChatCredits(data.cost_usd, hasTurnsWithoutCost)

  return (
    <Tooltip
      title={
        // Neither developers nor admins: credits, not what they are made of.
        !seesInternals ? (
          'Die Credits, die für diesen Chat bisher verbraucht wurden'
        ) : (
          <Box>
            <Typography variant="caption" component="div">
              Kosten dieses Chats bis zur letzten Antwort
            </Typography>
            <Typography variant="caption" component="div">
              {formatRequestCount(data.runs)} · {formatTokenCount(data.input_tokens)} Eingabe-Tokens
              · {formatTokenCount(data.output_tokens)} Ausgabe-Tokens
            </Typography>
            {hasTurnsWithoutCost && (
              <Typography variant="caption" component="div">
                Für {formatRequestCount(data.runs_without_cost)} ist kein Preis bekannt.
              </Typography>
            )}
          </Box>
        )
      }
    >
      <Chip
        size="small"
        variant="outlined"
        label={label}
        sx={{ opacity: isRunning ? 0.5 : 1, fontVariantNumeric: 'tabular-nums' }}
        data-cy="ai-chat-cost"
      />
    </Tooltip>
  )
}

export default ThreadCostChip
